<div class="flex h-full min-h-0 w-full flex-col overflow-y-auto scrollbar-transparent">
  <div class="shrink-0 space-y-4 p-4">
    <div>
      <h4 class="text-sm font-semibold text-primary">Sandbox user</h4>
      <p class="mt-1 text-xs text-secondary">
        Configure review-mode brand accounts: enable sandbox mode, set per-feature permissions, and
        restrict visible creators. Requires GodMode, Account Manager, or Creator Admin.
      </p>
    </div>

    <div class="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
      <div class="space-y-1 sm:col-span-2">
        <label for="sandbox-user-id" class="block text-sm font-medium text-primary"
          >User Firebase UID</label
        >
        <input
          id="sandbox-user-id"
          type="text"
          autocomplete="off"
          class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
          [value]="userIdInput()"
          (input)="onUserIdInput($any($event.target).value)"
          [disabled]="lookupLoading()"
        />
      </div>

      <div class="sm:col-span-2">
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          (click)="runUserLookup()"
          [disabled]="lookupLoading() || !userIdInput().trim()"
        >
          @if (lookupLoading()) {
            Looking up…
          } @else {
            Find user
          }
        </button>
      </div>
    </div>

    @if (lookupError()) {
    <p class="text-sm text-red-600 dark:text-red-400" role="alert">{{ lookupError() }}</p>
    }
  </div>

  @if (user(); as foundUser) {
  <div class="shrink-0 space-y-6 border-t border-zinc-200 p-4 dark:border-zinc-800">
    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">User details</h5>
      <dl class="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt class="font-medium text-secondary">Name</dt>
          <dd class="text-primary">{{ foundUser.firstName }} {{ foundUser.lastName }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Email</dt>
          <dd class="break-all text-primary">{{ foundUser.email }}</dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Created</dt>
          <dd class="text-primary">
            @if (foundUser.createdDate) {
              {{ formatDate(foundUser.createdDate) }}
            } @else {
              —
            }
          </dd>
        </div>
        <div>
          <dt class="font-medium text-secondary">Firebase UID</dt>
          <dd class="font-mono text-xs break-all text-primary">{{ foundUser.firebaseUid }}</dd>
        </div>
      </dl>

      <div class="mt-4 flex items-center gap-3">
        <label class="shout-toggle" for="sandbox-user-enabled">
          <input
            id="sandbox-user-enabled"
            type="checkbox"
            class="shout-toggle__input"
            [checked]="isSandboxEnabled()"
            [disabled]="sandboxToggleLoading()"
            (change)="onSandboxEnabledChange($any($event.target).checked)"
          />
          <span class="shout-toggle__slider" aria-hidden="true"></span>
        </label>
        <span class="text-sm font-medium text-primary">Sandbox user enabled</span>
      </div>
    </section>

    @if (isSandboxEnabled()) {
    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Feature access</h5>
      <p class="mt-0.5 text-xs text-secondary">
        Allowed when on. Briefs remain private; public publish stays blocked.
      </p>

      <ul class="mt-4 grid gap-3 sm:grid-cols-2">
        @for (flag of flagDefs; track flag.key) {
        <li class="flex items-start gap-3 rounded-lg border border-zinc-100 p-3 dark:border-zinc-800">
          <label class="shout-toggle mt-0.5" [for]="'sandbox-flag-' + flag.key">
            <input
              [id]="'sandbox-flag-' + flag.key"
              type="checkbox"
              class="shout-toggle__input"
              [checked]="isFlagEnabled(flag.key)"
              (change)="onFlagChange(flag.key, $any($event.target).checked)"
            />
            <span class="shout-toggle__slider" aria-hidden="true"></span>
          </label>
          <div class="min-w-0 flex-1">
            <label [for]="'sandbox-flag-' + flag.key" class="cursor-pointer text-sm font-medium text-primary">
              {{ flag.label }}
            </label>
            <p class="text-xs text-secondary">{{ flag.description }}</p>
          </div>
        </li>
        }
      </ul>
    </section>

    <section class="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <h5 class="text-sm font-semibold text-primary">Allowed creators</h5>
      <p class="mt-0.5 text-xs text-secondary">
        Optional allowlist. Leave empty to allow all creators when Find creators is enabled.
      </p>

      @if (selectedCreatorIds().length) {
      <ul class="mt-3 flex flex-wrap gap-2">
        @for (creatorId of selectedCreatorIds(); track creatorId) {
        <li>
          <button
            type="button"
            class="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs text-primary dark:border-zinc-700 dark:bg-zinc-900"
            (click)="removeSelectedCreator(creatorId)"
            [attr.aria-label]="'Remove ' + selectedCreatorDisplayLabel(creatorId)"
          >
            <span>{{ selectedCreatorDisplayLabel(creatorId) }}</span>
            <span aria-hidden="true">×</span>
          </button>
        </li>
        }
      </ul>
      }

      <div class="mt-4 grid gap-3 sm:grid-cols-2">
        <div class="space-y-1 sm:col-span-2">
          <label for="sandbox-creator-search" class="block text-sm font-medium text-primary"
            >Search creators</label
          >
          <input
            id="sandbox-creator-search"
            type="search"
            autocomplete="off"
            placeholder="Name, email, or Firebase UID"
            class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="creatorSearchInput()"
            (input)="onCreatorSearchInput($any($event.target).value)"
            [disabled]="creatorSearchLoading()"
          />
        </div>
        <div class="sm:col-span-2">
          <button
            type="button"
            class="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-primary disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950"
            (click)="runCreatorSearch()"
            [disabled]="creatorSearchLoading() || !creatorSearchInput().trim()"
          >
            @if (creatorSearchLoading()) {
              Searching…
            } @else {
              Search creators
            }
          </button>
        </div>
      </div>

      @if (creatorSearchError()) {
      <p class="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
        {{ creatorSearchError() }}
      </p>
      }

      @if (creatorSearchResults().length) {
      <ul class="mt-4 divide-y divide-zinc-100 rounded-lg border border-zinc-100 dark:divide-zinc-800 dark:border-zinc-800">
        @for (creator of creatorSearchResults(); track creator.blazeFirebaseUid) {
        <li class="flex items-start gap-3 p-3">
          <label class="shout-toggle mt-1" [for]="'creator-select-' + creator.blazeFirebaseUid">
            <input
              [id]="'creator-select-' + creator.blazeFirebaseUid"
              type="checkbox"
              class="shout-toggle__input"
              [checked]="isCreatorSelected(creator.blazeFirebaseUid)"
              (change)="onCreatorSelectionChange(creator, $any($event.target).checked)"
            />
            <span class="shout-toggle__slider" aria-hidden="true"></span>
          </label>
          <label [for]="'creator-select-' + creator.blazeFirebaseUid" class="min-w-0 flex-1 cursor-pointer">
            <span class="block text-sm font-medium text-primary">{{ creatorLabel(creator) }}</span>
            <span class="mt-0.5 block font-mono text-xs break-all text-secondary">{{
              creator.blazeFirebaseUid
            }}</span>
          </label>
        </li>
        }
      </ul>
      } @else if (!creatorSearchLoading() && creatorSearchInput().trim()) {
      <p class="mt-3 text-sm text-secondary">No creators matched that search.</p>
      }
    </section>

    <div>
      <button
        type="button"
        class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        (click)="saveAccess()"
        [disabled]="saveLoading()"
      >
        @if (saveLoading()) {
          Saving…
        } @else {
          Save sandbox access
        }
      </button>
    </div>
    }
  </div>
  }
</div>
